import { useEffect } from "react";
import { Rocket } from "lucide-react";

/**
 * SectorCompleteOverlay — full-screen cinematic shown after the pilot clears
 * the last level of a sector (every 500 levels). Auto-dismisses in 3.5s or
 * when clicked. Pulls the next sector's signature shape and renders it as a
 * giant neon grid centre-screen.
 *
 * Props:
 *   fromSector: { id, name }
 *   nextSector: { id, name } | null  (null = end of game)
 *   signatureShape: { id, label, cells } | null
 *   onDismiss: () => void
 */
export default function SectorCompleteOverlay({ fromSector, nextSector, signatureShape, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3800);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const ROWS = 8;
  const COLS = 12;
  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill(false));
  if (signatureShape?.cells) {
    for (const [r, c] of signatureShape.cells) {
      if (r >= 0 && r < ROWS && c >= 0 && c < COLS) grid[r][c] = true;
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center cursor-pointer"
      onClick={onDismiss}
      data-testid="sector-complete-overlay"
      style={{
        background: "radial-gradient(ellipse at center, rgba(176,38,255,0.35) 0%, rgba(0,0,0,0.92) 70%)",
        backdropFilter: "blur(4px)",
        animation: "fadeIn 0.4s ease-out",
      }}
    >
      <div className="text-center max-w-2xl px-6" style={{ animation: "fadeSlideIn 0.7s ease-out 0.1s both" }}>
        <div className="font-mono-cosmic text-[11px] tracking-[0.5em] text-[#00ff41] uppercase mb-3">
          // SECTOR CLEARED
        </div>
        <h1 className="font-heading font-black uppercase text-3xl sm:text-5xl md:text-6xl tracking-tighter mb-2 text-white leading-[0.9]">
          {fromSector?.name || "Sector"} Complete
        </h1>

        {nextSector ? (
          <>
            <div className="font-mono-cosmic text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#00ffff] uppercase mb-6">
              Warping into <span className="text-[#b026ff]">{nextSector.name}</span> space
            </div>

            {signatureShape && (
              <div className="mx-auto mb-6 w-full" style={{ maxWidth: "420px", padding: "0 1rem" }}>
                <div
                  className="grid gap-[3px] p-2 border border-[#b026ff]/60 bg-black/40"
                  style={{
                    gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                    aspectRatio: `${COLS} / ${ROWS}`,
                    boxShadow: "0 0 40px rgba(176,38,255,0.35)",
                  }}
                >
                  {grid.flat().map((on, i) => (
                    <span
                      key={i}
                      style={{
                        background: on ? "#b026ff" : "rgba(255,255,255,0.03)",
                        boxShadow: on ? "0 0 8px rgba(176,38,255,0.8)" : "none",
                        animation: on ? `cometPulse 1.6s ease-in-out infinite ${(i % 10) * 0.05}s` : "none",
                      }}
                    />
                  ))}
                </div>
                <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-white/60 uppercase mt-2">
                  Incoming · {signatureShape.label}
                </div>
              </div>
            )}

            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#b026ff] text-[#b026ff] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase">
              <Rocket className="w-3.5 h-3.5" />
              Tap anywhere to continue
            </div>
          </>
        ) : (
          <>
            <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#ffd700] uppercase mb-6">
              You've cleared all 3000 levels · You are a true Cosmic Grandmaster
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#ffd700] text-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.3em] uppercase">
              <Rocket className="w-3.5 h-3.5" />
              Tap anywhere to continue
            </div>
          </>
        )}
      </div>
    </div>
  );
}
